import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { ConsultationModal } from "@/components/ConsultationModal";
import { ChannelMark } from "@/components/ChannelMark";
import { Reveal, itemAnim, heroAnim } from "@/components/Reveal";
import { channels } from "@/data/channels";

const WHY_IMAGE = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1400&q=85";

const copy = {
  en: {
    nav: ["Our approach", "Services", "Estates", "About"],
    contact: "Talk to our team",
    eyebrow: "OUR DISTRIBUTION CHANNELS",
    heroTitle: <>Seen by the right guests, <em>everywhere</em> they book.</>,
    heroText: "Every Eden villa is listed across the world’s leading booking platforms and on our own website, managed from one calendar and one pricing strategy.",
    heroCta: "Get a free revenue assessment",
    caption: "Logos are trademarks of their respective owners.",
    howEyebrow: "HOW IT WORKS",
    how: [
      ["One calendar", "Every booking syncs instantly across all channels through our channel manager, so double bookings don’t happen."],
      ["One pricing strategy", "Dynamic pricing adjusts nightly rates by season, demand and channel, reviewed every day by our team."],
      ["One standard of care", "Wherever a guest books, they receive the same Eden welcome, housekeeping and support."],
    ],
    whyEyebrow: "WHY IT MATTERS FOR OWNERS",
    whyTitle: <>More reach. <em>Smarter</em> revenue.</>,
    whyPoints: [
      "Wider visibility in the markets that matter most for Bali.",
      "Listings optimised for each platform: photos, descriptions and reviews.",
      "A growing share of direct bookings, so you keep more of every stay.",
      "All performance by channel, clearly shown in your monthly Eden Report.",
    ],
    ctaTitle: <>Ready to put your villa <br /><em>in front of the world?</em></>,
    ctaText: "Tell us about your property. We’ll show you where it could perform best.",
    footer: "Keeping Bali’s finest estates in their best light.",
    footerDist: "Distribution Channels",
  },
  id: {
    nav: ["Pendekatan kami", "Layanan", "Properti", "Tentang"],
    contact: "Hubungi tim kami",
    eyebrow: "KANAL DISTRIBUSI KAMI",
    heroTitle: <>Dilihat tamu yang tepat, di <em>mana pun</em> mereka memesan.</>,
    heroText: "Setiap villa Eden terdaftar di platform pemesanan terkemuka dunia dan di situs kami sendiri — dikelola dari satu kalender dan satu strategi harga.",
    heroCta: "Dapatkan penilaian revenue gratis",
    caption: "Logo adalah merek dagang dari pemiliknya masing-masing.",
    howEyebrow: "CARA KERJANYA",
    how: [
      ["Satu kalender", "Setiap pemesanan tersinkron instan di semua kanal melalui channel manager kami, sehingga double booking tidak pernah terjadi."],
      ["Satu strategi harga", "Harga dinamis menyesuaikan tarif per malam berdasarkan musim, permintaan, dan kanal — ditinjau setiap hari oleh tim kami."],
      ["Satu standar layanan", "Di mana pun tamu memesan, mereka menerima sambutan, housekeeping, dan dukungan Eden yang sama."],
    ],
    whyEyebrow: "MENGAPA PENTING BAGI PEMILIK",
    whyTitle: <>Jangkauan lebih luas. Revenue lebih <em>cerdas.</em></>,
    whyPoints: [
      "Visibilitas lebih luas di pasar yang paling penting untuk Bali.",
      "Listing dioptimalkan untuk tiap platform: foto, deskripsi, dan ulasan.",
      "Porsi pemesanan langsung yang terus tumbuh — Anda menyimpan lebih banyak dari setiap masa inap.",
      "Seluruh performa per kanal tersaji jelas dalam Eden Report bulanan Anda.",
    ],
    ctaTitle: <>Siap menampilkan villa Anda <br /><em>ke seluruh dunia?</em></>,
    ctaText: "Ceritakan properti Anda. Kami akan tunjukkan di mana potensi terbaiknya.",
    footer: "Menjaga villa terbaik Bali dalam cahaya terbaiknya.",
    footerDist: "Kanal Distribusi",
  },
};

export default function Distribution({ lang, setLang }) {
  const [modalOpen, setModalOpen] = useState(false);
  const t = copy[lang];

  useEffect(() => {
    document.title = "Our Distribution Channels · Eden Hospitality Bali";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Eden villas are listed on Airbnb, Booking.com, Agoda, Expedia, Vrbo and our own site, managed from one calendar and one pricing strategy.");
  }, []);

  return <div className="site-shell">
    <Nav lang={lang} setLang={setLang} onConsult={() => setModalOpen(true)} labels={t.nav} ctaLabel={t.contact} />

    <main id="top">
      <section className="dist-hero" data-testid="dist-hero">
        <motion.p className="eyebrow" {...heroAnim(0.2)}>{t.eyebrow}</motion.p>
        <motion.h1 data-testid="dist-heading" {...heroAnim(0.35)}>{t.heroTitle}</motion.h1>
        <motion.p className="dist-hero-text" data-testid="dist-hero-text" {...heroAnim(0.55)}>{t.heroText}</motion.p>
        <motion.div {...heroAnim(0.75)}><button className="button button-terra" onClick={() => setModalOpen(true)} data-testid="dist-hero-cta">{t.heroCta} <ArrowUpRight size={16} /></button></motion.div>
      </section>

      <section className="channel-section" data-testid="channel-section">
        <div className="channel-grid">
          {channels.map((c, i) => (
            <motion.article className={`channel-card ${c.eden ? "channel-eden" : ""}`} key={c.id} data-testid={`channel-card-${c.id}`} {...itemAnim(i)}>
              <ChannelMark channel={c} />
              <p>{c.desc[lang]}</p>
            </motion.article>
          ))}
        </div>
        <p className="channel-caption" data-testid="channel-caption">{t.caption}</p>
      </section>

      <section className="how section" data-testid="how-section">
        <Reveal><p className="eyebrow">{t.howEyebrow}</p></Reveal>
        <div className="how-grid">
          {t.how.map(([title, text], i) => (
            <motion.div className="how-col" key={title} data-testid={`how-col-${i}`} {...itemAnim(i)}>
              <h3>{title}</h3><p>{text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="why" data-testid="why-section">
        <Reveal className="why-text">
          <p className="eyebrow">{t.whyEyebrow}</p>
          <h2>{t.whyTitle}</h2>
          <ul className="why-list">
            {t.whyPoints.map((point, i) => <li key={i} data-testid={`why-point-${i}`}><Check size={15} />{point}</li>)}
          </ul>
        </Reveal>
        <Reveal className="why-photo" delay={0.15}><div className="arch-photo" style={{ backgroundImage: `url(${WHY_IMAGE})` }} data-testid="why-arch-photo" /></Reveal>
      </section>

      <section className="closing section-dark" data-testid="dist-cta">
        <Reveal className="closing-content">
          <p className="eyebrow gold">EDEN HOSPITALITY</p>
          <h2>{t.ctaTitle}</h2>
          <p>{t.ctaText}</p>
          <button className="button button-light" onClick={() => setModalOpen(true)} data-testid="dist-closing-cta">{t.contact} <ArrowUpRight size={17} /></button>
        </Reveal>
        <div className="closing-orb" />
      </section>
    </main>

    <Footer footerText={t.footer} contactLabel={t.contact} distLabel={t.footerDist} onConsult={() => setModalOpen(true)} />
    <ConsultationModal lang={lang} open={modalOpen} onClose={() => setModalOpen(false)} ctaLabel={t.contact} />
  </div>;
}
